import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { closeType } from './errorInfomation';
import { LwUsers } from '../../services/lwUser/Base';
import { HttpError } from '../../services/lwUser/Error';
import CommonModal from '../modals/common/Modal';

/**
 * ベースとなるモーダル
 * @param {string} topClassName モーダルのクラス名
 * @param {string} title タイトル
 * @param {HTMLElement} renderBody body部分を表示するコンポーネント
 * @param {HTMLElement} renderFooter footer部分を表示するコンポーネント
 * @param {HTMLElement} renderErrorInfomation エラー情報を表示するコンポーネント
 *
 * NOTE:事前読み込みされると壊れるモーダルになるので、条件付きレンダーを用いて親コンポーネントで表示・非表示をコントロールするようにする。
 **/
function LwBaseModal({
  topClassName,
  title,
  renderBody,
  renderFooter,
  renderErrorInfomation,
}) {
  return (
    <CommonModal visible={true}>
      <div className={`LWBaseModal dialog dialog--modal ${topClassName}`}>
        <div className='dialog__header'>
          <span className='dialog__title'>{title}</span>
        </div>
        <div className='dialog__body'>{renderBody}</div>
        <div className='dialog__footer'>{renderFooter}</div>
        {renderErrorInfomation}
      </div>
    </CommonModal>
  );
}

const movedUseIdsType = {
  add: 'add',
  remove: 'remove',
};

/**
 * 2種類のユーザーデータを管理するカスタムフック
 * @param {function} getLeftUsers 左側のユーザーを取得するための関数(render毎の動作を防止するためuseCallbackで囲むこと)
 * @param {function} getRightUsers 右側のユーザーを取得するための関数(render毎の動作を防止するためuseCallbackで囲むこと)
 * @param {function} displayErrorInfomation エラー情報を表示する関数
 **/
function useDualUserData(getLeftUsers, getRightUsers, displayErrorInfomation) {
  const getUsers = useCallback(
    async () => {
      return Promise.all([getLeftUsers(), getRightUsers()]).then(results => {
        return results;
      });
    },
    [getLeftUsers, getRightUsers]
  );
  const error_action = useCallback(
    e => {
      if (e instanceof HttpError) {
        displayErrorInfomation(_.get(e, 'message', ''), closeType.modal_close);
      } else {
        throw e;
      }
    },
    [displayErrorInfomation]
  );
  const { data, isLodaing } = useFetchData(
    getUsers,
    [new LwUsers([]), new LwUsers([])],
    error_action
  );
  const searchedleftUsers = data[0];
  const searchedrightUsers = data[1];

  return {
    searchedleftUsers,
    searchedrightUsers,
    isLodaing,
  };
}

/**
 * 1種類のユーザーデータを管理するカスタムフック
 * @param {function} getUsers ユーザーを取得するための関数(render毎の動作を防止するためuseCallbackで囲むこと)
 * @param {function} displayErrorInfomation エラー情報を表示する関数
 **/
function useSingleUserData(getUsers, displayErrorInfomation) {
  const error_action = useCallback(
    e => {
      if (e instanceof HttpError) {
        displayErrorInfomation(_.get(e, 'message', ''), closeType.modal_close);
      } else {
        throw e;
      }
    },
    [displayErrorInfomation]
  );
  const { data: searchedUsers, isLodaing } = useFetchData(
    getUsers,
    new LwUsers([]),
    error_action
  );
  return { searchedUsers, isLodaing };
}

/**
 * データを取得するカスタムフック
 * @param {function} getData データを取得するための関数(必須、render毎の動作を防止するためuseCallbackで囲むこと)
 * @param {object} init_data 初期データ(必須、初回render時はこのデータを取得するので、エラーにならないようなオブジェクトを渡す)
 * @param {function} error_action エラー時のアクション(引数に例外オブジェクトを渡せること、render毎の動作を防止するためuseCallbackで囲むこと)
 **/
function useFetchData(getData, init_data, error_action = null) {
  const [data, setData] = useState(init_data);
  const [isLodaing, setIsLoding] = useState(false);

  // ingnoreについて：https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
  useEffect(
    () => {
      let ignore = false;
      const fetch_data = async () => {
        setIsLoding(true);
        try {
          const result = await getData();
          if (!ignore) {
            setData(result);
          }
        } catch (e) {
          if (error_action !== null) {
            error_action(e);
          } else {
            throw e;
          }
        } finally {
          setIsLoding(false);
        }
      };
      fetch_data();
      return () => {
        ignore = true;
      };
    },
    [getData, error_action]
  );
  return { data, isLodaing };
}

export {
  LwBaseModal,
  movedUseIdsType,
  useDualUserData,
  useSingleUserData,
  useFetchData,
};



// WEBPACK FOOTER //
// ./src/components/lwUser/topModalCommon.jsx